import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout, hasConflicts, LinkToEntity, renderBadge } from "@/src/components/cards/common";
import { PatientAddressField } from "@/src/components/cards/patient-card/patient-address-field";
import PatientCardEditable from "@/src/components/cards/patient-card/patient-card-editable";
import { PATIENT_HEADER_FIELD_KEYS, PatientCardTitle, getPatientCardDetails, withResolvedEhrIdFields } from "@/src/components/cards/patient-card/patient-card-header";
import ErrorAlert from "@/src/components/error-alert";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { ValerieCard } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canAccessInbox, canWritePatient } from "@/src/lib/rbac";
import { displayEhrId, displayEhrIdentifiers } from "@/src/lib/resolve-ehr-ids";
import { getFormattedAddress } from "@/src/models/address";
import { Patient } from "@/src/models/patient";
export const PATIENT_FIELDS: Field[] = [{
  key: "familyName",
  label: "Family Name",
  comparable: true,
  required: true,
  section: "Demographics"
}, {
  key: "givenName",
  label: "Given Name",
  comparable: true,
  required: true,
  section: "Demographics"
}, {
  key: "middleName",
  label: "Middle Name",
  comparable: true,
  section: "Demographics"
}, {
  key: "dateOfBirth",
  label: "Date of Birth",
  type: "date",
  comparable: true,
  section: "Demographics"
}, {
  key: "gender",
  label: "Gender",
  comparable: true,
  section: "Demographics"
}, {
  key: "race",
  label: "Race",
  comparable: false,
  section: "Demographics"
}, {
  key: "ethnicity",
  label: "Ethnicity",
  comparable: false,
  section: "Demographics"
}, {
  key: "language",
  label: "Language",
  comparable: false,
  section: "Demographics"
}, {
  key: "type",
  label: "Type",
  comparable: false,
  section: "Demographics"
}, {
  key: "ehrId",
  label: "EHR ID",
  comparable: false,
  section: "Identifiers",
  getValue: (p: Patient) => displayEhrId(p)
}, {
  key: "homeAddress.line1",
  label: "Address",
  comparable: true,
  colSpan: 2,
  section: "Address"
}, {
  key: "homeAddress.line2",
  label: "Address 2",
  comparable: true,
  section: "Address"
}, {
  key: "homeAddress.city",
  label: "City",
  comparable: true,
  startsRow: true,
  section: "Address"
}, {
  key: "homeAddress.state",
  label: "State",
  comparable: true,
  section: "Address"
}, {
  key: "homeAddress.postalCode",
  label: "Zip Code",
  comparable: true,
  section: "Address"
}, {
  key: "homePhoneNumber",
  label: "Home Phone",
  type: "tel",
  comparable: true,
  section: "Contact"
}, {
  key: "mobilePhoneNumber",
  label: "Mobile Phone",
  type: "tel",
  comparable: true,
  section: "Contact"
}, {
  key: "personalEmail",
  label: "Email",
  comparable: true,
  section: "Contact"
}];
const ADDRESS_FIELDS = PATIENT_FIELDS.filter(field => field.section === "Address");
const COMBINED_ADDRESS_FIELD: Field = {
  key: "homeAddress",
  label: "Address",
  comparable: true,
  section: "Address",
  getValue: (p: Patient) => getFormattedAddress(p.homeAddress)
};
function getCompareFields(fields: Field[], patient: Patient, secondaryPatient: Patient | null) {
  return fields.flatMap(field => {
    if (field.section === "Address") {
      return field === ADDRESS_FIELDS[0] ? [COMBINED_ADDRESS_FIELD] : [];
    }
    if (PATIENT_HEADER_FIELD_KEYS.has(field.key) && !(field.comparable && secondaryPatient && hasConflicts([field], patient, secondaryPatient))) {
      return [];
    }
    return [field];
  });
}
export default function PatientCard(t0) {
  const $ = _c(40);
  const {
    patient,
    compareMode: t1,
    secondaryPatient: t2,
    alwaysShowLink: t3
  } = t0;
  const compareMode = t1 === undefined ? false : t1;
  const secondaryPatient = t2 === undefined ? null : t2;
  const alwaysShowLink = t3 === undefined ? false : t3;
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const {
    group,
    roles
  } = useUser();
  let t4;
  if ($[0] !== roles) {
    t4 = canWritePatient(roles);
    $[0] = roles;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const canWrite = t4;
  let t5;
  if ($[2] !== roles) {
    t5 = canAccessInbox(roles);
    $[2] = roles;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const canContact = t5;
  let t6;
  if ($[4] !== group) {
    t6 = p => displayEhrIdentifiers(p, group);
    $[4] = group;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const resolveIdentifiers = t6;
  const fields = withResolvedEhrIdFields(PATIENT_FIELDS, resolveIdentifiers);
  let t7;
  let t8;
  if ($[6] !== patient || $[7] !== secondaryPatient) {
    t7 = () => {
      logger.info("Patient in patient card", {
        patient,
        secondaryPatient
      });
    };
    t8 = [patient, secondaryPatient];
    $[6] = patient;
    $[7] = secondaryPatient;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let hasAnyConflicts = false;
  if (compareMode && secondaryPatient) {
    hasAnyConflicts = hasConflicts(fields, patient, secondaryPatient);
  }
  let t9;
  if ($[10] !== isEditing) {
    t9 = () => {
      setIsEditing(!isEditing);
    };
    $[10] = isEditing;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleEditToggle = t9;
  if (isEditing) {
    let t10;
    if ($[12] !== compareMode || $[13] !== handleEditToggle || $[14] !== patient || $[15] !== secondaryPatient) {
      t10 = <PatientCardEditable patient={patient} onClose={handleEditToggle} compareMode={compareMode} secondaryObject={secondaryPatient} fields={PATIENT_FIELDS} />;
      $[12] = compareMode;
      $[13] = handleEditToggle;
      $[14] = patient;
      $[15] = secondaryPatient;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    return t10;
  }
  const t10 = `/patients/${patient.displayId}`;
  let t11;
  if ($[17] !== handleEditToggle || $[18] !== patient.id || $[19] !== t10) {
    t11 = createCardActionsProps({
      id: patient.id,
      label: "Patient ID",
      href: t10,
      customProps: [{
        label: "Edit Patient",
        onClick: handleEditToggle
      }]
    });
    $[17] = handleEditToggle;
    $[18] = patient.id;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const cardActionsProps = t11;
  let t12;
  if ($[21] !== patient) {
    t12 = patient ? <PatientCardTitle patient={patient} /> : "Patient";
    $[21] = patient;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== patient || $[24] !== resolveIdentifiers) {
    t13 = patient ? getPatientCardDetails(patient, resolveIdentifiers(patient)) : undefined;
    $[23] = patient;
    $[24] = resolveIdentifiers;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== alwaysShowLink || $[27] !== pathname || $[28] !== patient) {
    t14 = patient && (alwaysShowLink || !pathname?.startsWith(`/patients/${patient.displayId}`)) ? <LinkToEntity href={`/patients/${patient.displayId}`} /> : null;
    $[26] = alwaysShowLink;
    $[27] = pathname;
    $[28] = patient;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== canContact || $[31] !== canWrite || $[32] !== cardActionsProps || $[33] !== patient || $[34] !== router) {
    t15 = canWrite && patient && <EntityDropdownMenu cardActionsProps={cardActionsProps} customProps={canContact ? [{
      label: "Contact Patient",
      onClick: () => router.push(`/inbox/${patient.displayId}`)
    }] : []} />;
    $[30] = canContact;
    $[31] = canWrite;
    $[32] = cardActionsProps;
    $[33] = patient;
    $[34] = router;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== canWrite || $[37] !== cardActionsProps || $[38] !== patient) {
    t16 = canWrite && patient && <CardActions {...cardActionsProps} isContextMenu={true} />;
    $[36] = canWrite;
    $[37] = cardActionsProps;
    $[38] = patient;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return <ContextMenu><ContextMenuTrigger asChild={true}><ValerieCard className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" tabIndex={0} title={t12} details={t13} actions={<>{patient && renderBadge(compareMode, secondaryPatient, hasAnyConflicts)}{t14}{t15}</>}>{!patient ? <ErrorAlert message="Patient not found" compact={true} /> : <FieldLayout fields={compareMode ? getCompareFields(fields, patient, secondaryPatient) : fields.filter(_temp)} patient={patient} secondaryObject={secondaryPatient} compareMode={compareMode} renderField={field_0 => compareMode && field_0 === COMBINED_ADDRESS_FIELD ? <PatientAddressField address={patient.homeAddress} secondaryAddress={secondaryPatient?.homeAddress ?? null} hasConflict={!!secondaryPatient && hasConflicts(ADDRESS_FIELDS, patient, secondaryPatient)} /> : undefined} />}</ValerieCard></ContextMenuTrigger>{t16}</ContextMenu>;
}
function _temp(field) {
  return !PATIENT_HEADER_FIELD_KEYS.has(field.key);
}
function GridItemSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function PatientCardSkeleton() {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-6 w-48" />;
    t1 = <Skeleton className="mt-1 h-4 w-56" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ValerieCard className="w-full" title={t0} description={t1}><div className="grid grid-cols-3 gap-4">{Array.from({
          length: 6
        }, _temp2)}</div></ValerieCard>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp2(_, index) {
  return <GridItemSkeleton key={index} />;
}